import { env } from '@/env.mjs';

/**
 * Send an Experiment 4 lead to the marketing spreadsheet.
 *
 * WHY A SPREADSHEET, AND WHY THIS IS TEMPORARY. There is no `public/labs-leads` endpoint, and building
 * one is a backend PR plus a schema decision on someone else's team. Marketing needs the addresses in
 * Customer.io this week, not next sprint. A Google Apps Script web app appending rows to a Sheet needs
 * no backend work at all, and Customer.io imports from a Sheet directly.
 *
 * 🔴 THIS IS A TEMPORARY DATA STORE HOLDING PERSONAL DATA, and it should be treated as one. The SOC 2
 * Type 1 audit is dated 2026-09-01 and data retention is in scope, so a spreadsheet of student email
 * addresses is exactly the kind of store an auditor will ask to see governed. Two things follow:
 * the sheet belongs in a shared Drive with real access control rather than an individual's Drive, and
 * this should be replaced by a real endpoint rather than left to become permanent.
 *
 * WHY `sendBeacon` AND NOT `fetch`. This fires at the exact moment the page navigates to sign-up. A
 * normal `fetch` is cancelled when the document unloads, so a share of leads would silently vanish,
 * and the ones lost would be the FASTEST converters, biasing the very number the experiment reports.
 * `sendBeacon` is built for this: the browser owns the request and completes it after the page is
 * gone. It also sends without a CORS preflight, which matters because Apps Script web apps do not
 * answer preflight requests.
 *
 * The trade is that `sendBeacon` gives no response, so a failure is invisible here. That is acceptable
 * because the lead is ALSO recorded as a `labs_preview_email_submitted` analytics event: the sheet is
 * the marketing copy of the data, the event is the measurement copy, and the two can be reconciled.
 */

export interface PreviewLead {
  archetype: string;
  email: string;
  /** The `?src=` slug of the guide page that sent them, or 'direct'. */
  guideSource: string;
  /**
   * Whether they ticked the OPTIONAL marketing consent box. Recorded truthfully either way: the
   * readout send is transactional, and requiring marketing consent to receive your own work would be
   * consent bundling. Customer.io filters on this column.
   */
  marketingConsent: boolean;
  vertical: string;
  /** The anonymous browser id, so a sheet row can be joined to the analytics funnel. */
  visitorId: string | null;
}

/**
 * Fire the lead at the sheet. Never throws and never blocks.
 *
 * Returns whether the browser accepted the request for delivery, which is NOT whether the row landed.
 * Callers should not branch on it beyond diagnostics: a reader must reach sign-up whether or not
 * marketing's spreadsheet is reachable.
 */
/**
 * 🔴 THIS ENDPOINT MUST NEVER SEND MAIL. It is a public, unauthenticated Apps Script web app whose URL
 * ships in the client bundle (`NEXT_PUBLIC_`), so anyone who reads the bundle can POST to it. An
 * earlier version of this file also sent the rendered readout and a subject line, and the documented
 * script mailed them: that turned the endpoint into an open relay able to send attacker-chosen HTML,
 * as Extern, to any address until the Workspace quota ran out. Found by Bugbot on #2843.
 *
 * The blast radius of what remains is junk rows in a spreadsheet, which is the level of exposure this
 * shortcut was justified at. Emailing the readout belongs behind something authenticated: send it from
 * Customer.io off the sheet, not from here.
 */

/**
 * Whether the lead pipe exists in this build. The payoff copy branches on it so the page only promises
 * an inbox copy when something can actually send one: unset in local dev, Storybook and preview
 * deployments, set in production once the Apps Script is deployed.
 */
export function isLeadPipeConfigured(): boolean {
  return Boolean(env.NEXT_PUBLIC_LABS_PREVIEW_LEAD_URL);
}

export function submitPreviewLead(lead: PreviewLead): boolean {
  const endpoint = env.NEXT_PUBLIC_LABS_PREVIEW_LEAD_URL;

  // Unset in local dev, Storybook and preview deployments, which is the point: those runs must not
  // write into the real sheet. The preview still works and still emits its events.
  if (!endpoint) {
    return false;
  }

  if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') {
    return false;
  }

  const payload = JSON.stringify({
    archetype: lead.archetype,
    email: lead.email,
    guide_source: lead.guideSource,
    marketing_consent: lead.marketingConsent,
    // Stamped client-side because Apps Script's own clock is in the script's timezone, not the
    // reader's, and the sheet is read by people who care when a lead arrived.
    submitted_at: new Date().toISOString(),
    vertical: lead.vertical,
    visitor_id: lead.visitorId,
  });

  try {
    // `text/plain` is deliberate. A JSON content type makes this a non-simple request, which triggers
    // a CORS preflight that Apps Script will not answer, and the beacon is dropped. Apps Script reads
    // the raw body out of `e.postData.contents` either way.
    return navigator.sendBeacon(endpoint, new Blob([payload], { type: 'text/plain;charset=UTF-8' }));
  } catch {
    // A blocked beacon must never cost the reader their signup.
    return false;
  }
}
